/**
 * mpScenarioParamUtil.js
 *
 * MP(Master Planning) 전용 시나리오 파라미터 순수 함수.
 * (공용 함수는 scenarioParamUtil.js 참조)
 */

// 현재값 기반 초기값 제안 — 능력 상향 파라미터(가동시간/가동일수/효율)만 대상.
//   선택 대상(target)들의 현재값(t.current[param_key]) MAX 기준 +15%, 상한 클램프.
//   비대상 파라미터·대상 미선택·current 부재 시 fallback(정적 기본값) 반환.
export const SUGGEST_FACTOR = 1.15;
const NUDGE_UP = new Set(["work_hour_per_day", "work_day_per_week", "efficiency"]);

// 수율·자원 효율: 저장 소수/배수 → 표시 %(수율 0.97→97, 효율 1.2→120). 빈 값은 "".
export const yieldDecToPct = (dec) =>
  (dec == null || dec === "") ? "" : Number((Number(dec) * 100).toFixed(2));

export function suggestValue(def, scope, targets, { numMax, fallback } = {}) {
  if (!def || !NUDGE_UP.has(def.param_key)) return fallback;            // 비대상 파라미터
  if (!Array.isArray(targets) || targets.length === 0) return fallback; // scope=all / 미선택

  const curs = targets
    .map(t => t?.current?.[def.param_key])
    .filter(v => v != null && !Number.isNaN(Number(v)))
    .map(Number);
  if (curs.length === 0) return fallback;        // 대상에 current 없음 → fallback

  const base = Math.max(...curs);                // 가장 제약이 큰 대상 기준(단일 값이 전체 대상에 적용)
  const raw  = base * SUGGEST_FACTOR;            // +15%
  let suggested = def.type === "multiplier"
    ? Math.ceil(raw * 10) / 10                   // 효율: 0.1 단위 올림 (1.0 → 1.2)
    : Math.ceil(raw);                            // 시간/일수: 정수 올림 (20 → 23)
  if (numMax != null) suggested = Math.min(suggested, numMax);  // 상한 클램프 (24h/7일)
  return String(suggested);
}

// ── 적용 기간(periods) 구간 겹침 판정 ────────────────────────────────────────
// period = { start_dt: "yyyy-MM-dd", end_dt: "yyyy-MM-dd", value }
// 'yyyy-MM-dd' 문자열은 사전식 비교가 곧 날짜 비교라 문자열 비교를 그대로 쓴다.
// 겹치는(또는 시작>종료인) 구간의 index Set 반환. 경계 동일(같은 날)도 겹침으로 본다.
export function findOverlappingPeriodIndices(periods) {
  const bad = new Set();
  if (!Array.isArray(periods)) return bad;
  // 둘 다 입력된 유효 구간만 비교 대상(한쪽만 채운 입력 중 구간은 겹침 판정 제외)
  const items = periods.map((p, idx) => ({ idx, s: p?.start_dt || "", e: p?.end_dt || "" }));
  // 시작>종료 자체도 오류 구간으로 표시
  items.forEach((it) => { if (it.s && it.e && it.s > it.e) bad.add(it.idx); });
  for (let i = 0; i < items.length; i++) {
    const a = items[i];
    if (!a.s || !a.e || a.s > a.e) continue;
    for (let j = i + 1; j < items.length; j++) {
      const b = items[j];
      if (!b.s || !b.e || b.s > b.e) continue;
      // 두 구간 겹침: a.start <= b.end && b.start <= a.end (경계 동일 포함)
      if (a.s <= b.e && b.s <= a.e) { bad.add(a.idx); bad.add(b.idx); }
    }
  }
  return bad;
}

// 어떤 override 의 periods 에든 겹침(또는 시작>종료)이 있으면 true.
// override 단위 periods 와 자원별(targets[].periods, 자원 그리드) 모두 검사.
// periods 없는 override/모듈(RP·IP 등)은 자연히 false → 제너릭 안전.
export function overridesHavePeriodConflict(overrides) {
  if (!Array.isArray(overrides)) return false;
  return overrides.some((ov) =>
    findOverlappingPeriodIndices(ov?.periods).size > 0
    || (Array.isArray(ov?.targets)
        && ov.targets.some((t) => findOverlappingPeriodIndices(t?.periods).size > 0)));
}

// ── 로트 하한/상한 역전(min > max) 판정 ──────────────────────────────────────
// 화면에서 한 행으로 함께 편집하므로 min/max override 는 scope_type·대상이 같다.
// 반환 키 = `${scope_type}\u0000${cd}`(scope=all 은 cd="") — 어긋난 입력칸을 물들이는 데 쓴다.
// min/max lot 이 아닌 override(RP·IP 등)는 자연히 빈 Set → 제너릭 안전.
const LOT_MIN_KEY = "min_lot_size";
const LOT_MAX_KEY = "max_lot_size";

// override 하나를 { 대상코드 → 값 } 으로 편다. scope=all 은 cd="" 한 건.
function _lotBoundValues(ov) {
  const out = new Map();
  if (!ov) return out;
  const scope = ov.scope_type || "all";
  if (scope === "all") {
    const n = Number(ov.value);
    if (ov.value !== "" && ov.value != null && Number.isFinite(n)) out.set("", n);
    return out;
  }
  for (const t of (ov.targets || [])) {
    if (!t?.cd) continue;
    // 대상별 값이 비면 override 단일값이 그 대상에도 적용된다(단일값 편집 경로).
    const raw = (t.value === "" || t.value == null) ? ov.value : t.value;
    const n = Number(raw);
    if (raw !== "" && raw != null && Number.isFinite(n)) out.set(t.cd, n);
  }
  return out;
}

export function findLotBoundConflicts(overrides) {
  const bad = new Set();
  if (!Array.isArray(overrides)) return bad;
  const mins = overrides.filter((o) => o?.param_key === LOT_MIN_KEY);
  const maxs = overrides.filter((o) => o?.param_key === LOT_MAX_KEY);
  if (mins.length === 0 || maxs.length === 0) return bad;

  for (const mn of mins) {
    for (const mx of maxs) {
      // 스코프가 다르면 대상 집합이 겹치는지 화면에서 확정할 수 없다 → 엔진 경고에 맡긴다.
      if ((mn.scope_type || "all") !== (mx.scope_type || "all")) continue;
      const mnVals = _lotBoundValues(mn);
      const mxVals = _lotBoundValues(mx);
      for (const [cd, minV] of mnVals) {
        const maxV = mxVals.get(cd);
        // 0 은 '제약 해제' 의사표시 — 역전으로 보지 않는다.
        if (maxV == null || minV <= 0 || maxV <= 0) continue;
        if (minV > maxV) bad.add(`${mn.scope_type || "all"}\u0000${cd}`);
      }
    }
  }
  return bad;
}

export function overridesHaveLotBoundConflict(overrides) {
  return findLotBoundConflicts(overrides).size > 0;
}

// ── 자원 능력(RC) 전체=무한 → 무의미해진 자원 파라미터 제외 ─────────────────
// 엔진은 RC=무한(IC)이어도 자원 캘린더를 재계산하므로, 가동시간·효율 override 를 그대로
// 보내면 z_resource_util.capa_total 이 부풀어 무한계획 진단의 '필요 증설량'이 과소로 나온다.
// 화면(편집기)에서 숨기는 것과 같은 판정 — 전송·미리보기도 여기서 맞춘다.
// param_key 없는 override(RP·IP 형태)는 그대로 통과 → 제너릭 안전.
const RC_VOIDED_KEYS_FALLBACK = new Set(
  ["efficiency", "work_hour_per_day", "over_work_hour_per_day", "work_day_per_week",
   "holiday_calendar"]);

export function dropOverridesVoidedByRcInfinite(overrides, paramDefs) {
  if (!Array.isArray(overrides)) return overrides;
  const rcInfinite = overrides.some((o) => o?.param_key === "RC"
    && (o.scope_type || "all") === "all" && String(o.value) === "infinite");
  if (!rcInfinite) return overrides;
  // 그룹 판정은 백엔드 param_defs[].group 이 정본, 미보유 시에만 정적 키 폴백.
  const groupOf = new Map((Array.isArray(paramDefs) ? paramDefs : [])
    .filter((d) => d?.param_key).map((d) => [d.param_key, d.group]));
  const voided = (pk) => (groupOf.has(pk)
    ? groupOf.get(pk) === "resource" : RC_VOIDED_KEYS_FALLBACK.has(pk));
  return overrides.filter((o) => !(o?.param_key && o.param_key !== "RC" && voided(o.param_key)));
}

// ── 자원 능력 수치 파라미터 — 자원·워크센터 스코프에서 대상별 행 그리드로 편집 ──────────
export const MP_RESOURCE_NUM = new Set(["efficiency", "work_hour_per_day", "over_work_hour_per_day",
                                        "work_day_per_week"]);

// 합성 행(row_group) 멤버 키 — 대표가 맨 앞. 행의 적용 단위·표시 행은 대표 기준으로 저장된다.
export function rowGroupKeys(paramDefs, pk) {
  const defs = Array.isArray(paramDefs) ? paramDefs : [];
  const g = defs.find(d => d?.param_key === pk)?.row_group;
  if (!g) return [pk];
  const members = defs.filter(d => d?.row_group === g).map(d => d.param_key);
  const primary = defs.find(d => d?.row_group === g && d?.row_role === "primary")?.param_key;
  return primary ? [primary, ...members.filter(k => k !== primary)] : members;
}

// 대상 엔트리에서 값·구간을 뺀 식별자만 — 동반 키에 대표의 값이 흘러들지 않게.
export const stripTargetValues = (targets) => (Array.isArray(targets) ? targets : [])
  .map(({ cd, name, loc_cd, item_cd }) =>
    ({ cd, name, ...(loc_cd != null ? { loc_cd } : {}), ...(item_cd != null ? { item_cd } : {}) }));

// 행 그룹 멤버 전원의 현재 스코프 대상을 기억 — 대상별 값은 각 멤버의 override 에만 있다.
export function rememberRowScopeTargets(mem, members, scope, ovOf) {
  const next = { ...(mem || {}) };
  (members || []).forEach(pk => { next[`${pk}:${scope}`] = ovOf(pk)?.targets || []; });
  return next;
}

// 동반 키 패치 — 적용 단위만 대표를 따르고 대상은 자기 기억을 복원한다.
//   기억이 없을 때만 대표의 대상을 식별자만 빌린다(로트 3종은 칩 집합이 멤버 공유).
export function companionScopePatches(members, primary, scope, mem, ovOf, primaryTargets) {
  const out = {};
  (members || []).forEach(pk => {
    if (pk === primary || !ovOf(pk)) return;
    const remembered = mem?.[`${pk}:${scope}`];
    out[pk] = { scope_type: scope,
                targets: scope === "all" ? [] : (remembered || stripTargetValues(primaryTargets)) };
  });
  return out;
}

// 일괄값(o.value)을 값 없는 기존 대상에 확정 — 한 대상만 고쳐도 나머지가 마스터로 돌아가지 않게.
export function seedTargetsFromValue(o, tgts) {
  const v = o?.value;
  if (v == null || v === "") return tgts;
  return tgts.map(t => (t.value == null || t.value === "") ? { ...t, value: v } : t);
}

// 자원 그리드에 서 있는 행 — 드롭다운 선택(__numPick) 우선, 미설정이면 문제 대상 전체. 후보 풀 객체 반환.
export function shownResourceCds(scenario, pk, scope) {
  const st = scenario?.scope_targets || {};
  const all = st[`${scope}_all`]?.length ? st[`${scope}_all`] : (st[scope] || []);
  const byCd = new Map(all.map(t => [t.cd, t]));
  const picked = scenario?.__numPick?.[`${pk}:${scope}`] ?? (st[scope] || []).map(t => t.cd);
  return picked.filter(cd => byCd.has(cd)).map(cd => byCd.get(cd));
}

// 챗 값 → 표시 중인 대상 전체에 대상별 값으로 기록(periods 보존). 표시 행이 없으면 null.
export function applyChatValueToShownTargets(ov, shownCands, value) {
  if (!Array.isArray(shownCands) || shownCands.length === 0) return null;
  const tgts = seedTargetsFromValue(ov, Array.isArray(ov?.targets) ? [...ov.targets] : []);
  shownCands.forEach(c => {
    const base = { cd: c.cd, name: c.name, ...(c.loc_cd != null ? { loc_cd: c.loc_cd } : {}), value };
    const i = tgts.findIndex(t => t.cd === c.cd);
    if (i >= 0) tgts[i] = { ...tgts[i], ...base }; else tgts.push(base);
  });
  return { targets: tgts, value: null };
}

// 대상 제외 — 일괄값은 targets 에만 적용되므로(param_mapper→applier) 대상만 빼면 충분하다.
export function dropTargetCds(ov, cds) {
  const drop = new Set(cds);
  const targets = (Array.isArray(ov?.targets) ? ov.targets : []).filter(t => !drop.has(t.cd));
  const next = { ...ov, targets };
  // 대상이 하나도 안 남으면 일괄값·구간은 걸릴 곳이 없다 — 화면은 '조정됨', 전송은 드롭으로 갈리는 걸 막는다.
  if (targets.length === 0 && (ov?.scope_type || "all") !== "all") {
    next.value = null;
    if (Array.isArray(ov?.periods) && ov.periods.length > 0) next.periods = [];
  }
  return next;
}
